(function () {
  const elementName = 'nuora-community-carousel';

  if (window.customElements.get(elementName)) return;

  class NuoraCommunityCarousel extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.heightFrame = 0;
      this.pendingHeightIndex = 0;
      this.scrollEndTimer = 0;
      this.scrollStartIndex = null;
      this.programmaticTarget = null;
      this.lastTrackWidth = 0;
      this.resizeObserver = null;
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleScrollEnd = this.handleScrollEnd.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      if (this.dataset.ready === 'true' || this.dataset.ipInitPending === 'true') return;

      // IP-2026-07-14-change: only the audited target opts into near-viewport initialization; every existing non-target carousel keeps eager behavior.
      const shouldDefer = this.hasAttribute('data-ip-defer-init')
        && !window.Shopify?.designMode
        && 'IntersectionObserver' in window;

      if (!shouldDefer) {
        this.initializeCarousel();
        return;
      }

      this.dataset.ipInitPending = 'true';
      this.initObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.initObserver?.disconnect();
        this.initObserver = null;
        if (!this.isConnected) return;
        this.initializeCarousel();
      }, { rootMargin: '800px 0px' });
      // IP-2026-07-14-change: observe the contained section box, which remains measurable even while content-visibility skips its descendants.
      this.initObserver.observe(this.closest('[data-nuora-below-fold]') || this);
    }

    // IP-2026-07-14-change: geometry reads and per-card observers now start only when useful on the target route.
    initializeCarousel() {
      if (this.dataset.ready === 'true') return;
      delete this.dataset.ipInitPending;

      this.track = this.querySelector('[data-community-track]');
      this.cards = Array.from(this.querySelectorAll('[data-community-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-community-dot]'));
      this.previousButton = this.querySelector('[data-community-arrow="previous"]');
      this.nextButton = this.querySelector('[data-community-arrow="next"]');

      if (!this.track || !this.cards.length || this.cards.length !== this.dots.length) return;

      this.dataset.ready = 'true';
      this.addEventListener('click', this.handleClick);
      this.track.addEventListener('keydown', this.handleKeydown);
      this.track.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
      this.track.addEventListener('wheel', this.handleWheel, { passive: true });
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('scrollend', this.handleScrollEnd);
      window.addEventListener('resize', this.handleResize);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      this.lastTrackWidth = Math.round(this.track.getBoundingClientRect().width);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleHeight());
        this.cards.forEach((card) => this.resizeObserver.observe(card));
      }

      this.goTo(0, 'auto');
    }

    disconnectedCallback() {
      // IP-2026-07-14-change: release a pending viewport observer when Theme Editor or navigation removes the section before initialization.
      if (this.initObserver) {
        this.initObserver.disconnect();
        this.initObserver = null;
      }
      delete this.dataset.ipInitPending;

      if (this.dataset.ready !== 'true') return;

      this.removeEventListener('click', this.handleClick);
      this.track.removeEventListener('keydown', this.handleKeydown);
      this.track.removeEventListener('pointerdown', this.handlePointerDown);
      this.track.removeEventListener('wheel', this.handleWheel);
      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('scrollend', this.handleScrollEnd);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      // IP-2026-07-14-change: clear stored timer/frame ids as well as cancelling them so a Theme Editor reconnect can schedule fresh layout work.
      if (this.scrollEndTimer) window.clearTimeout(this.scrollEndTimer);
      if (this.heightFrame) window.cancelAnimationFrame(this.heightFrame);
      this.scrollEndTimer = 0;
      this.heightFrame = 0;
      delete this.dataset.ready;
    }

    clamp(index) {
      return Math.max(0, Math.min(this.cards.length - 1, index));
    }

    setActive(index) {
      this.activeIndex = this.clamp(index);

      this.cards.forEach((card, cardIndex) => {
        card.classList.toggle('is-active', cardIndex === this.activeIndex);
      });

      this.dots.forEach((dot, dotIndex) => {
        const selected = dotIndex === this.activeIndex;
        dot.classList.toggle('is-active', selected);
        if (selected) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.previousButton) {
        this.previousButton.disabled = this.cards.length < 2 || this.activeIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.cards.length < 2 || this.activeIndex === this.cards.length - 1;
      }

      this.scheduleHeight();
    }

    slidePosition(index) {
      const firstCard = this.cards[0];
      const card = this.cards[this.clamp(index)];

      if (!firstCard || !card) return 0;
      return card.offsetLeft - firstCard.offsetLeft;
    }

    nearestIndex() {
      const currentPosition = this.track.scrollLeft;
      let nearest = this.activeIndex;
      let distance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const nextDistance = Math.abs(this.slidePosition(index) - currentPosition);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = index;
        }
      });

      return nearest;
    }

    goTo(index, behavior) {
      const nextIndex = this.clamp(index);
      const nextPosition = this.slidePosition(nextIndex);
      const scrollBehavior = behavior || (this.reduceMotionQuery.matches ? 'auto' : 'smooth');

      this.clearScrollEndTimer();
      this.scrollStartIndex = null;
      this.programmaticTarget = nextIndex;
      this.track.classList.remove('is-user-scrolling');
      this.setActive(nextIndex);

      if (Math.abs(this.track.scrollLeft - nextPosition) <= 1) {
        this.finishScroll(nextIndex);
        return;
      }

      this.track.scrollTo({
        left: nextPosition,
        behavior: scrollBehavior
      });

      this.queueScrollEnd(scrollBehavior === 'smooth' ? 180 : 80);
    }

    toggleExtras(button, forceOpen) {
      if (!button) return;

      const slide = button.closest('[data-community-slide]');
      if (!slide) return;

      const slideIndex = this.cards.indexOf(slide);
      if (slideIndex < 0) return;

      const extraComments = Array.from(slide.querySelectorAll('[data-community-extra]'));
      if (!extraComments.length) return;

      const open = typeof forceOpen === 'boolean'
        ? forceOpen
        : button.getAttribute('aria-expanded') !== 'true';

      this.goTo(slideIndex, 'auto');

      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? button.dataset.collapseLabel : button.dataset.expandLabel;
      slide.classList.toggle('is-expanded', open);

      extraComments.forEach((comment) => {
        comment.hidden = !open;
      });

      this.scheduleHeight(slideIndex);
    }

    scheduleHeight(index) {
      if (!this.track) return;

      this.pendingHeightIndex = this.clamp(
        typeof index === 'number' ? index : this.activeIndex
      );

      if (this.heightFrame) return;

      this.heightFrame = window.requestAnimationFrame(() => {
        const activeCard = this.cards[this.pendingHeightIndex];
        if (activeCard) this.track.style.height = `${Math.ceil(activeCard.scrollHeight)}px`;
        this.heightFrame = 0;
      });
    }

    clearScrollEndTimer() {
      if (!this.scrollEndTimer) return;
      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = 0;
    }

    queueScrollEnd(delay) {
      this.clearScrollEndTimer();
      this.scrollEndTimer = window.setTimeout(() => {
        this.scrollEndTimer = 0;
        this.handleScrollEnd();
      }, delay || 140);
    }

    beginManualScroll() {
      this.clearScrollEndTimer();

      if (this.scrollStartIndex === null) {
        this.scrollStartIndex = this.programmaticTarget === null
          ? this.activeIndex
          : this.nearestIndex();
      }

      this.programmaticTarget = null;
      this.track.classList.add('is-user-scrolling');
    }

    finishScroll(index) {
      const nextIndex = this.clamp(index);
      this.clearScrollEndTimer();
      this.scrollStartIndex = null;
      this.programmaticTarget = null;
      this.track.classList.remove('is-user-scrolling');
      this.setActive(nextIndex);
    }

    handleClick(event) {
      const dot = event.target.closest('[data-community-dot]');
      if (dot && this.contains(dot)) {
        this.goTo(Number(dot.dataset.communityDot || 0));
        return;
      }

      const arrow = event.target.closest('[data-community-arrow]');
      if (arrow && this.contains(arrow)) {
        this.goTo(this.activeIndex + (arrow.dataset.communityArrow === 'previous' ? -1 : 1));
        return;
      }

      const more = event.target.closest('[data-community-more]');
      if (more && this.contains(more)) this.toggleExtras(more);
    }

    handleKeydown(event) {
      if (event.target !== this.track) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.goTo(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }

    handlePointerDown(event) {
      if (event.isPrimary === false) return;
      if (typeof event.button === 'number' && event.button !== 0) return;
      this.beginManualScroll();
    }

    handleWheel(event) {
      if (Math.abs(event.deltaX) < 1 && !event.shiftKey) return;
      this.beginManualScroll();
    }

    handleScroll() {
      if (this.programmaticTarget === null && this.scrollStartIndex === null) {
        this.scrollStartIndex = this.activeIndex;
        this.track.classList.add('is-user-scrolling');
      }

      this.queueScrollEnd(140);
    }

    handleScrollEnd() {
      if (!this.track || !this.cards.length) return;

      this.clearScrollEndTimer();

      let nextIndex;
      if (this.programmaticTarget !== null) {
        nextIndex = this.programmaticTarget;
      } else {
        const startIndex = this.scrollStartIndex === null
          ? this.activeIndex
          : this.scrollStartIndex;
        const nearest = this.nearestIndex();
        nextIndex = Math.max(startIndex - 1, Math.min(startIndex + 1, nearest));
      }

      nextIndex = this.clamp(nextIndex);
      const nextPosition = this.slidePosition(nextIndex);

      if (Math.abs(this.track.scrollLeft - nextPosition) > 1) {
        this.scrollStartIndex = null;
        this.programmaticTarget = nextIndex;
        this.setActive(nextIndex);
        this.track.scrollTo({ left: nextPosition, behavior: 'auto' });
        this.queueScrollEnd(80);
        return;
      }

      this.finishScroll(nextIndex);
    }

    handleResize() {
      if (!this.track) return;

      const nextTrackWidth = Math.round(this.track.getBoundingClientRect().width);
      if (nextTrackWidth === this.lastTrackWidth) return;

      this.lastTrackWidth = nextTrackWidth;
      this.goTo(this.activeIndex, 'auto');
      this.scheduleHeight(this.activeIndex);
    }

    handleBlockSelect(event) {
      if (!event.target || !this.contains(event.target)) return;

      const comment = event.target.closest('[data-community-comment]');
      if (!comment) return;

      const slide = comment.closest('[data-community-slide]');
      if (!slide) return;

      if (comment.hasAttribute('data-community-extra') && comment.hidden) {
        this.toggleExtras(slide.querySelector('[data-community-more]'), true);
      }

      const slideIndex = this.cards.indexOf(slide);
      if (slideIndex >= 0) this.goTo(slideIndex, 'auto');
    }
  }

  window.customElements.define(elementName, NuoraCommunityCarousel);
})();
